/**
 * Toast — single toast system for the whole app (ported from SK Catalog).
 * ──────────────────────────────────────────────────────────────────────────
 * Every toast (useToast, showSuccess, handleApiError) renders through the
 * shared `showToast` (react-native-toast-message) with the premium config in
 * toastConfig.tsx, mounted via <ToastHost/> in App.tsx.
 *
 * `useToast` is a thin compatibility layer so `const toast = useToast()` call
 * sites read naturally — their methods delegate to `showToast`.
 *
 * API:
 *   const toast = useToast();
 *   toast.success(msg, opts?)   toast.error(msg, opts?)
 *   toast.warn(msg, opts?)      toast.info(msg, opts?)
 *   toast(...)  // callable: ({ tone, title, message }) or a plain string
 *   toast.dismiss()
 */
import { showToast } from './toastComponent';

export type ToastTone = 'success' | 'warn' | 'danger' | 'info';

export interface ToastOptions {
  tone?: ToastTone;
  title?: string;
  message?: string;
  duration?: number;
}

export interface ToastApi {
  (opts: ToastOptions | string): string;
  success: (msg: string, opts?: Omit<ToastOptions, 'tone'>) => string;
  error: (msg: string, opts?: Omit<ToastOptions, 'tone'>) => string;
  warn: (msg: string, opts?: Omit<ToastOptions, 'tone'>) => string;
  info: (msg: string, opts?: Omit<ToastOptions, 'tone'>) => string;
  dismiss: (id?: string) => void;
}

// Map our tones onto react-native-toast-message's success | error | info types
// (warn → error styling, as a cautionary message).
function toType(tone?: ToastTone): 'success' | 'error' | 'info' {
  if (tone === 'danger' || tone === 'warn') return 'error';
  if (tone === 'info') return 'info';
  return 'success';
}

function fire(type: 'success' | 'error' | 'info', msg: string, sub?: string): string {
  showToast(type, msg, sub);
  return '';
}

const api = ((opts: ToastOptions | string): string => {
  if (typeof opts === 'string') return fire('success', opts);
  const text1 = opts.title ?? opts.message ?? '';
  const text2 = opts.title ? opts.message : undefined;
  return fire(toType(opts.tone), text1, text2);
}) as ToastApi;

api.success = (msg, opts) => fire('success', msg, opts?.message);
api.error = (msg, opts) => fire('error', msg, opts?.message);
api.warn = (msg, opts) => fire('error', msg, opts?.message);
api.info = (msg, opts) => fire('info', msg, opts?.message);
api.dismiss = () => { /* react-native-toast-message auto-hides; no-op */ };

export function useToast(): ToastApi {
  return api;
}
